import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { 
  Search, Calendar, Clock, User, Eye, Heart, MessageCircle, 
  ArrowRight, Grid, List 
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import type { BlogPost, BlogCategory } from '../../types/blog';

// Sample blog posts (in a real app, this would come from an API)
const sampleBlogPosts: BlogPost[] = [
  {
    id: '1',
    title: 'The Future of Artificial Intelligence in Healthcare: Transforming Patient Care Through Innovation',
    slug: 'future-ai-healthcare-patient-care',
    excerpt: 'Explore how AI is revolutionizing healthcare delivery, from diagnostic accuracy to personalized treatment plans, and what this means for the future of medicine.',
    content: '',
    author: {
      name: 'Dr. Amira Hassan',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b47c?w=150&h=150&fit=crop&crop=face',
      bio: 'Chief AI Researcher at amasQIS with 15+ years in healthcare AI'
    },
    publishedDate: '2024-01-15T10:00:00Z',
    readingTime: 8,
    tags: ['Artificial Intelligence', 'Healthcare', 'Machine Learning', 'Medical Technology', 'Innovation'],
    category: 'Healthcare AI',
    image: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
    likes: 245,
    comments: 18,
    views: 3420,
    featured: true,
    status: 'published'
  },
  {
    id: '2',
    title: 'Building Scalable IoT Solutions: From Edge Computing to Cloud Integration',
    slug: 'scalable-iot-solutions-edge-cloud',
    excerpt: 'Learn how to design and implement IoT architectures that scale from small deployments to enterprise-level solutions, covering edge computing, data processing, and cloud integration.',
    content: '',
    author: {
      name: 'Mohamed Al-Rashid',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
      bio: 'Senior IoT Architect with expertise in edge computing and cloud platforms'
    },
    publishedDate: '2024-01-12T14:30:00Z',
    readingTime: 12,
    tags: ['IoT', 'Edge Computing', 'Cloud Architecture', 'Scalability', 'System Design'],
    category: 'IoT & Infrastructure',
    image: 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
    likes: 189,
    comments: 24,
    views: 2856,
    featured: false,
    status: 'published'
  },
  {
    id: '3',
    title: 'Machine Learning Model Deployment: From Development to Production',
    slug: 'ml-model-deployment-production',
    excerpt: 'A comprehensive guide to deploying machine learning models in production environments, covering containerization, monitoring, and MLOps best practices.',
    content: '',
    author: {
      name: 'Sarah Thompson',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face',
      bio: 'MLOps Engineer specializing in production ML systems'
    },
    publishedDate: '2024-01-10T09:15:00Z',
    readingTime: 10,
    tags: ['Machine Learning', 'MLOps', 'DevOps', 'Deployment', 'Monitoring'],
    category: 'AI & ML',
    image: 'https://images.unsplash.com/photo-1555949963-ff9fe719c7a1?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
    likes: 156,
    comments: 12,
    views: 2134,
    featured: false,
    status: 'published'
  },
  {
    id: '4',
    title: 'Cybersecurity in the Age of AI: Protecting Digital Assets with Smart Defense',
    slug: 'cybersecurity-ai-smart-defense',
    excerpt: 'Discover how artificial intelligence is transforming cybersecurity, from threat detection to automated response systems, and learn about the latest security challenges in AI-driven environments.',
    content: '',
    author: {
      name: 'Ahmed Al-Mansouri',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
      bio: 'Cybersecurity Specialist with focus on AI-driven security solutions'
    },
    publishedDate: '2024-01-08T16:45:00Z',
    readingTime: 9,
    tags: ['Cybersecurity', 'AI Security', 'Threat Detection', 'Data Protection', 'Digital Defense'],
    category: 'Security',
    image: 'https://images.unsplash.com/photo-1563206767-5b18f218e8de?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
    likes: 203,
    comments: 31,
    views: 2987,
    featured: true,
    status: 'published'
  },
  {
    id: '5',
    title: 'Digital Transformation in Manufacturing: Industry 4.0 Success Stories',
    slug: 'digital-transformation-manufacturing-industry-4',
    excerpt: 'Real-world case studies of how manufacturers are leveraging IoT, AI, and automation to optimize operations, reduce costs, and improve product quality in the era of Industry 4.0.',
    content: '',
    author: {
      name: 'Mohamed Al-Rashid',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
      bio: 'Senior IoT Architect with expertise in edge computing and cloud platforms'
    },
    publishedDate: '2024-01-05T11:20:00Z',
    readingTime: 15,
    tags: ['Digital Transformation', 'Manufacturing', 'Industry 4.0', 'Automation', 'Case Studies'],
    category: 'Manufacturing',
    image: 'https://images.unsplash.com/photo-1565514020179-026b92b84bb6?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
    likes: 134,
    comments: 19,
    views: 1876,
    featured: false,
    status: 'published'
  },
  {
    id: '6',
    title: 'The Rise of Quantum Computing: Implications for AI and Machine Learning',
    slug: 'quantum-computing-ai-machine-learning',
    excerpt: 'Exploring the potential of quantum computing to revolutionize AI and machine learning, from quantum algorithms to hybrid classical-quantum systems.',
    content: '',
    author: {
      name: 'Dr. Amira Hassan',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b47c?w=150&h=150&fit=crop&crop=face',
      bio: 'Chief AI Researcher at amasQIS with 15+ years in healthcare AI'
    },
    publishedDate: '2024-01-03T13:30:00Z',
    readingTime: 11,
    tags: ['Quantum Computing', 'AI', 'Machine Learning', 'Quantum Algorithms', 'Future Technology'],
    category: 'Emerging Tech',
    image: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
    likes: 287,
    comments: 42,
    views: 4123,
    featured: true,
    status: 'published'
  }
];

const categories: BlogCategory[] = [
  { id: 'all', name: 'All Categories', slug: 'all', description: '', color: 'gray', count: sampleBlogPosts.length },
  { id: 'healthcare-ai', name: 'Healthcare AI', slug: 'healthcare-ai', description: '', color: 'blue', count: 2 },
  { id: 'iot-infrastructure', name: 'IoT & Infrastructure', slug: 'iot-infrastructure', description: '', color: 'green', count: 1 },
  { id: 'ai-ml', name: 'AI & ML', slug: 'ai-ml', description: '', color: 'purple', count: 1 },
  { id: 'security', name: 'Security', slug: 'security', description: '', color: 'red', count: 1 },
  { id: 'manufacturing', name: 'Manufacturing', slug: 'manufacturing', description: '', color: 'orange', count: 1 },
  { id: 'emerging-tech', name: 'Emerging Tech', slug: 'emerging-tech', description: '', color: 'teal', count: 1 },
];

export function BlogListPage() {
  const [posts] = useState<BlogPost[]>(sampleBlogPosts);
  const [filteredPosts, setFilteredPosts] = useState<BlogPost[]>(sampleBlogPosts);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Filter and search posts
  useEffect(() => {
    let filtered = [...posts];

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(post => 
        post.category.toLowerCase().replace(/\s+/g, '-') === selectedCategory ||
        post.category.toLowerCase().includes(selectedCategory.replace('-', ' '))
      );
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(post =>
        post.title.toLowerCase().includes(query) ||
        post.excerpt.toLowerCase().includes(query) ||
        post.tags.some(tag => tag.toLowerCase().includes(query)) ||
        post.author.name.toLowerCase().includes(query)
      );
    }

    // Sort posts
    switch (sortBy) {
      case 'newest':
        filtered.sort((a, b) => new Date(b.publishedDate).getTime() - new Date(a.publishedDate).getTime());
        break;
      case 'oldest':
        filtered.sort((a, b) => new Date(a.publishedDate).getTime() - new Date(b.publishedDate).getTime());
        break;
      case 'popular':
        filtered.sort((a, b) => ((b.views || 0) + (b.likes || 0)) - ((a.views || 0) + (a.likes || 0)));
        break;
      case 'trending':
        filtered.sort((a, b) => (b.likes || 0) - (a.likes || 0));
        break;
    }

    setFilteredPosts(filtered);
  }, [posts, selectedCategory, searchQuery, sortBy]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatReadTime = (readingTime: number) => {
    return `${readingTime} min read`;
  };

  const featuredPosts = filteredPosts.filter(post => post.featured);
  const regularPosts = filteredPosts.filter(post => !post.featured);

  return (
    <div className="pt-20 min-h-screen">
      {/* Hero Section */}
      <motion.section
        className="relative py-15 overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="container mx-auto px-6 text-center">
          <motion.h1 
            className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-primary via-accent to-secondary bg-clip-text text-transparent"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            amasQIS Blog
          </motion.h1>
          <motion.p 
            className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            Insights, innovations, and industry expertise from our team of AI and technology specialists. 
            Stay updated with the latest trends in artificial intelligence, IoT, and digital transformation.
          </motion.p>
        </div>
      </motion.section>

      {/* Search and Filters */}
      <section className="py-8 border-b border-border/50">
        <div className="container mx-auto px-6">
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search articles, tags, or authors..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 glass border-border/50 focus:border-primary"
              />
            </div>

            {/* Filters */}
            <div className="flex items-center gap-4">
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-[180px] glass border-border/50">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent className="dropdown-bg">
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.slug}>
                      {category.name} ({category.count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[140px] glass border-border/50">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent className="dropdown-bg">
                  <SelectItem value="newest">Newest</SelectItem>
                  <SelectItem value="oldest">Oldest</SelectItem>
                  <SelectItem value="popular">Popular</SelectItem>
                  <SelectItem value="trending">Trending</SelectItem>
                </SelectContent>
              </Select>

              <div className="flex items-center border border-border/50 rounded-lg p-1 glass">
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('grid')}
                  className="p-2"
                >
                  <Grid className="w-4 h-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className="p-2"
                >
                  <List className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Active Filters */}
          {(searchQuery || selectedCategory !== 'all') && (
            <div className="flex items-center gap-2 mt-4">
              <span className="text-sm text-muted-foreground">Active filters:</span>
              {searchQuery && (
                <Badge variant="secondary" className="bg-primary/20 text-primary">
                  Search: "{searchQuery}"
                  <button 
                    onClick={() => setSearchQuery('')}
                    className="ml-2 hover:text-primary/80"
                  >
                    ×
                  </button>
                </Badge>
              )}
              {selectedCategory !== 'all' && (
                <Badge variant="secondary" className="bg-accent/20 text-accent">
                  {categories.find(c => c.slug === selectedCategory)?.name}
                  <button 
                    onClick={() => setSelectedCategory('all')}
                    className="ml-2 hover:text-accent/80"
                  >
                    ×
                  </button>
                </Badge>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Featured Posts */}
      {featuredPosts.length > 0 && selectedCategory === 'all' && !searchQuery && (
        <section className="py-12">
          <div className="container mx-auto px-6">
            <h2 className="text-3xl font-semibold mb-8">Featured Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredPosts.map((post, index) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card className="glass p-0 overflow-hidden hover:border-primary/50 transition-all duration-300 group">
                    {post.image && (
                      <div className="relative h-48 overflow-hidden">
                        <img 
                          src={post.image} 
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute top-4 left-4">
                          <Badge className="bg-primary text-primary-foreground">
                            Featured
                          </Badge>
                        </div>
                      </div>
                    )}
                    <div className="p-6">
                      <Badge variant="outline" className="border-primary/50 text-primary mb-3">
                        {post.category}
                      </Badge>
                      <h3 className="font-semibold mb-3 line-clamp-2 group-hover:text-primary transition-colors">
                        {post.title}
                      </h3>
                      <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>
                      
                      <div className="flex items-center text-xs text-muted-foreground mb-4">
                        <div className="flex items-center gap-1 mr-4">
                          <User className="w-3 h-3" />
                          {post.author.name}
                        </div>
                        <div className="flex items-center gap-1 mr-4">
                          <Calendar className="w-3 h-3" />
                          {formatDate(post.publishedDate)}
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatReadTime(post.readingTime)}
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {post.views || 0}
                          </div>
                          <div className="flex items-center gap-1">
                            <Heart className="w-3 h-3" />
                            {post.likes || 0}
                          </div>
                          <div className="flex items-center gap-1">
                            <MessageCircle className="w-3 h-3" />
                            {post.comments || 0}
                          </div>
                        </div>
                        
                        <Link
                          to={`/blog/${post.slug}`}
                          className="text-primary hover:text-primary/80 text-sm font-medium inline-flex items-center group-hover:gap-2 transition-all"
                        >
                          Read More
                          <ArrowRight className="w-3 h-3 ml-1 group-hover:translate-x-1 transition-transform" />
                        </Link>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Regular Posts */}
      <section className="py-12">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-semibold">
              {searchQuery ? `Search Results (${filteredPosts.length})` : 
               selectedCategory !== 'all' ? categories.find(c => c.slug === selectedCategory)?.name : 
               'Latest Articles'}
            </h2>
            
            {filteredPosts.length > 0 && (
              <span className="text-sm text-muted-foreground">
                Showing {filteredPosts.length} {filteredPosts.length === 1 ? 'article' : 'articles'}
              </span>
            )}
          </div>

          {filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📝</div>
              <h3 className="text-xl font-semibold mb-2">No articles found</h3>
              <p className="text-muted-foreground mb-6">
                {searchQuery 
                  ? `No articles match your search for "${searchQuery}"`
                  : 'No articles found in this category'
                }
              </p>
              <Button 
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('all');
                }}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                Clear Filters
              </Button>
            </div>
          ) : (
            <div className={
              viewMode === 'grid' 
                ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'
                : 'space-y-6'
            }>
              {(featuredPosts.length > 0 && selectedCategory === 'all' && !searchQuery ? regularPosts : filteredPosts).map((post, index) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card className={`glass p-0 overflow-hidden hover:border-primary/50 transition-all duration-300 group ${
                    viewMode === 'list' ? 'flex flex-row' : ''
                  }`}>
                    {post.image && (
                      <div className={`relative overflow-hidden ${
                        viewMode === 'list' ? 'w-48 h-32 flex-shrink-0' : 'h-48'
                      }`}>
                        <img 
                          src={post.image} 
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    <div className="p-6 flex-1">
                      <Badge variant="outline" className="border-primary/50 text-primary mb-3">
                        {post.category}
                      </Badge>
                      <h3 className={`font-semibold mb-3 group-hover:text-primary transition-colors ${
                        viewMode === 'list' ? 'line-clamp-2 text-lg' : 'line-clamp-2'
                      }`}>
                        {post.title}
                      </h3>
                      <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>
                      
                      <div className="flex items-center text-xs text-muted-foreground mb-4">
                        <div className="flex items-center gap-1 mr-4">
                          <User className="w-3 h-3" />
                          {post.author.name}
                        </div>
                        <div className="flex items-center gap-1 mr-4">
                          <Calendar className="w-3 h-3" />
                          {formatDate(post.publishedDate)}
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatReadTime(post.readingTime)}
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {post.views || 0}
                          </div>
                          <div className="flex items-center gap-1">
                            <Heart className="w-3 h-3" />
                            {post.likes || 0}
                          </div>
                          <div className="flex items-center gap-1">
                            <MessageCircle className="w-3 h-3" />
                            {post.comments || 0}
                          </div>
                        </div>
                        
                        <Link
                          to={`/blog/${post.slug}`}
                          className="text-primary hover:text-primary/80 text-sm font-medium inline-flex items-center group-hover:gap-2 transition-all"
                        >
                          Read More
                          <ArrowRight className="w-3 h-3 ml-1 group-hover:translate-x-1 transition-transform" />
                        </Link>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}